import { useState, useMemo, useCallback } from 'react';
import { toast } from 'sonner';
import { aiService, isWarningBlocking, type AnomalyWarning } from '@/services/aiService';
import type { Timesheet, TimesheetEntry, WorkingPattern, ExtendedWorkingPattern } from '@/types/timesheet';
import type { OrganisationTimesheetSettings } from '@/types/timesheetSettings';

export interface UseAiWarningsParams {
  entries: TimesheetEntry[];
  currentWeekStart: Date;
  timesheets: Timesheet[];
  effectiveWorkingPattern: WorkingPattern | ExtendedWorkingPattern | undefined;
  organizationSettings: OrganisationTimesheetSettings | undefined;
}

export interface UseAiWarningsResult {
  aiWarnings: AnomalyWarning[];
  hasBlockingPolicyViolation: boolean;
  nonDismissableWarningIds: Set<string>;
  handleDismissWarning: (warningId: string) => void;
}

/**
 * Computes the AI anomaly warnings for the current timesheet week and manages
 * user dismissals. Raw (unfiltered) warnings drive the blocking-submission
 * check so dismissals cannot bypass it, while the policy-filtered set (minus
 * dismissed ids) is what gets displayed.
 */
export function useAiWarnings({
  entries,
  currentWeekStart,
  timesheets,
  effectiveWorkingPattern,
  organizationSettings,
}: UseAiWarningsParams): UseAiWarningsResult {
  const [dismissedWarnings, setDismissedWarnings] = useState<Set<string>>(new Set());

  // Truly raw warnings — before applyPolicyViolationSettings, used for blocking checks.
  // This ensures that e.g. an overtime-risk warning whose primary check type
  // (legal-weekly-maximum) is disabled is still visible to the blocking logic
  // which can fall back to the weekly-overage check.
  const rawAiWarnings = useMemo(() => {
    return aiService.detectAnomalies(
      entries, 
      currentWeekStart, 
      timesheets,
      effectiveWorkingPattern,
      organizationSettings,
      { skipPolicyFiltering: true }
    );
  }, [entries, currentWeekStart, timesheets, effectiveWorkingPattern, organizationSettings]);

  // Policy-filtered warnings for display (suppression, consolidation, severity overrides applied)
  const policyFilteredWarnings = useMemo(() => {
    return aiService.detectAnomalies(
      entries, 
      currentWeekStart, 
      timesheets,
      effectiveWorkingPattern,
      organizationSettings
    );
  }, [entries, currentWeekStart, timesheets, effectiveWorkingPattern, organizationSettings]);

  // Filtered warnings for display (dismissed ones removed)
  const aiWarnings = useMemo(() => {
    return policyFilteredWarnings.filter(w => !dismissedWarnings.has(w.id));
  }, [policyFilteredWarnings, dismissedWarnings]);

  // Blocking check uses RAW (unfiltered) warnings so dismissals cannot bypass it
  const hasBlockingPolicyViolation = useMemo(() => {
    const checks = organizationSettings?.policyViolations?.checks;
    if (!checks || rawAiWarnings.length === 0) return false;
    const monitoringFrequency = organizationSettings?.policyViolations?.monitoringFrequency ?? 'week';
    return rawAiWarnings.some(warning => isWarningBlocking(warning, checks, monitoringFrequency));
  }, [rawAiWarnings, organizationSettings?.policyViolations?.checks, organizationSettings?.policyViolations?.monitoringFrequency]);

  // Set of warning IDs that correspond to blocking policy checks (non-dismissable).
  // Uses policyFilteredWarnings (the displayed set) so IDs match the UI.
  const nonDismissableWarningIds = useMemo(() => {
    const checks = organizationSettings?.policyViolations?.checks;
    if (!checks) return new Set<string>();
    const monitoringFrequency = organizationSettings?.policyViolations?.monitoringFrequency ?? 'week';
    const ids = new Set<string>();
    policyFilteredWarnings.forEach(w => {
      if (isWarningBlocking(w, checks, monitoringFrequency)) ids.add(w.id);
    });
    return ids;
  }, [policyFilteredWarnings, organizationSettings?.policyViolations?.checks, organizationSettings?.policyViolations?.monitoringFrequency]);

  const handleDismissWarning = useCallback((warningId: string) => {
    if (nonDismissableWarningIds.has(warningId)) {
      toast.warning('This alert cannot be dismissed because it is blocking submission per your organisation policy.');
      return;
    }
    setDismissedWarnings(prev => new Set([...prev, warningId]));
  }, [nonDismissableWarningIds]);

  return {
    aiWarnings,
    hasBlockingPolicyViolation,
    nonDismissableWarningIds,
    handleDismissWarning,
  };
}
